/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import api from "@/lib/api";
import EditorialCard from "@/components/EditorialCard";
import SectionHeader from "@/components/SectionHeader";
import AnimatedCounter from "@/components/AnimatedCounter";
import useSiteSettings from "@/hooks/useSiteSettings";
import { toast } from "sonner";

const HomePage = () => {
  const settings = useSiteSettings();
  const [articles, setArticles] = useState([]);
  const [news, setNews] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, n, i, m] = await Promise.all([
          api.articles({ limit: 6 }),
          api.news({ limit: 6 }),
          api.interviews({ limit: 4 }),
          api.magazines(),
        ]);
        setArticles(a); setNews(n); setInterviews(i); setMagazines(m);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await api.newsletter(email);
      toast.success("Welcome. Your first dispatch arrives Saturday.");
      setEmail("");
    } catch { toast.error("Subscription failed. Please try again."); }
    finally { setSubscribing(false); }
  };

  const hp = settings?.homepage || {};
  const hero = hp.hero || {};
  const orderedSections = useMemo(() => (hp.sections || []).filter((s) => s.enabled), [hp.sections]);
  const sectionMeta = (key) => orderedSections.find((s) => s.key === key) || null;

  const [lead, ...rest] = articles;
  const featuredMag = magazines[0];

  const renderers = {
    featured: () => {
      const meta = sectionMeta("featured");
      if (!meta) return null;
      return (
        <section key="featured" className="py-16 md:py-24 max-w-[1440px] mx-auto px-5 md:px-10" data-testid="featured-articles-section">
          <SectionHeader
            overline={meta.overline}
            title={meta.title}
            lede={meta.lede}
            action={
              <Link to="/articles" data-testid="featured-view-all" className="text-ink text-sm font-medium tracking-wide hover:text-gold-dark inline-flex items-center gap-2 border-b border-ink pb-0.5">
                View all articles <ArrowRight size={14} />
              </Link>
            }
          />
          {lead && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
              <div className="lg:col-span-8">
                <motion.article
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="editorial-card overflow-hidden group"
                  data-testid="featured-lead-article"
                >
                  <Link to={`/articles/${lead.slug}`} className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img src={lead.image} alt="" className="card-image absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 px-3 py-1 bg-paper/95 backdrop-blur-sm overline overline-muted">{lead.category}</div>
                    </div>
                    <div className="p-8 md:p-10 flex flex-col">
                      <div className="overline mb-4">Lead Story</div>
                      <h3 className="font-display text-ink text-3xl md:text-4xl leading-[1.08] mb-4">{lead.title}</h3>
                      <p className="text-ink-soft text-[15px] leading-relaxed line-clamp-4">{lead.excerpt}</p>
                      <div className="mt-auto pt-8 flex items-center justify-between text-xs font-mono-editorial text-ink-muted">
                        <div>{lead.author} · {new Date(lead.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>
                        <div>{lead.read_time}</div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              </div>
              <div className="lg:col-span-4 grid gap-8 grid-cols-1">
                {rest.slice(0, 2).map((a, i) => (
                  <EditorialCard key={a.id} number={String(i + 2).padStart(2, "0")} overline={a.category} title={a.title} excerpt={a.excerpt} image={a.image} meta={`${a.author} · ${a.read_time}`} href={`/articles/${a.slug}`} imageAspect="aspect-[16/10]" testId={`featured-secondary-${i}`} />
                ))}
              </div>
            </div>
          )}
          {rest.length > 2 && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
              {rest.slice(2, 5).map((a, i) => (
                <EditorialCard key={a.id} number={String(i + 4).padStart(2, "0")} overline={a.category} title={a.title} excerpt={a.excerpt} image={a.image} meta={`${a.author} · ${a.read_time}`} href={`/articles/${a.slug}`} testId={`featured-tertiary-${i}`} />
              ))}
            </div>
          )}
        </section>
      );
    },
    magazines: () => {
      const meta = sectionMeta("magazines");
      if (!meta) return null;
      return (
        <section key="magazines" className="py-16 md:py-24 bg-cream border-y border-ink-line" data-testid="magazine-featured-section">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <SectionHeader
              overline={meta.overline}
              title={meta.title}
              lede={meta.lede}
              action={<Link to="/magazine" data-testid="magazine-view-all" className="text-ink text-sm font-medium tracking-wide inline-flex items-center gap-2 border-b border-ink pb-0.5">All editions <ArrowRight size={14} /></Link>}
            />
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
              {magazines.map((m, i) => (
                <motion.div key={m.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: (i % 4) * 0.08 }} className="group" data-testid={`home-magazine-${i}`}>
                  <a href={m.pdf_url || "#"} target={m.pdf_url ? "_blank" : undefined} rel="noreferrer" onClick={(e) => { if (!m.pdf_url) e.preventDefault(); }} aria-label={m.title} className="block cursor-pointer">
                    <div className="relative aspect-[4/5] overflow-hidden bg-paper shadow-[0_30px_60px_-25px_rgba(15,15,16,0.28)] border border-ink-line">
                      <img src={m.cover_image} alt={`${m.title} cover`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]" />
                    </div>
                  </a>
                  <h3 className="mt-5 font-display text-ink text-lg md:text-xl leading-[1.2] group-hover:text-gold-dark transition-colors text-center">
                    <a href={m.pdf_url || "#"} target={m.pdf_url ? "_blank" : undefined} rel="noreferrer" onClick={(e) => { if (!m.pdf_url) e.preventDefault(); }} className="cursor-pointer">
                      {m.caption || m.title}
                    </a>
                  </h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );
    },
    news: () => {
      const meta = sectionMeta("news");
      if (!meta) return null;
      return (
        <section key="news" className="py-16 md:py-24 max-w-[1440px] mx-auto px-5 md:px-10" data-testid="latest-news-section">
          <SectionHeader
            overline={meta.overline}
            title={meta.title}
            lede={meta.lede}
            action={<Link to="/news" data-testid="news-view-all" className="text-ink text-sm font-medium tracking-wide hover:text-gold-dark inline-flex items-center gap-2 border-b border-ink pb-0.5">All news <ArrowRight size={14} /></Link>}
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.slice(0, 6).map((n, i) => (
              <EditorialCard key={n.id} overline={n.category} title={n.title} excerpt={n.summary} image={n.image} meta={`${new Date(n.date).toLocaleDateString(undefined,{month:"short",day:"numeric"})} · ${n.read_time}`} href={`/news/${n.slug}`} testId={`home-news-card-${i}`} />
            ))}
          </div>
        </section>
      );
    },
    interviews: () => {
      const meta = sectionMeta("interviews");
      if (!meta) return null;
      return (
        <section key="interviews" className="py-16 md:py-24 bg-cream border-y border-ink-line" data-testid="interviews-section">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <SectionHeader
              overline={meta.overline}
              title={meta.title}
              lede={meta.lede}
              action={<Link to="/interviews" data-testid="interviews-view-all" className="text-ink text-sm font-medium tracking-wide hover:text-gold-dark inline-flex items-center gap-2 border-b border-ink pb-0.5">All interviews <ArrowRight size={14} /></Link>}
            />
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {interviews.slice(0, 4).map((it, i) => (
                <motion.div key={it.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }} className="editorial-card overflow-hidden" data-testid={`home-interview-card-${i}`}>
                  <Link to={`/interviews/${it.slug}`} className="block group">
                    <div className="relative aspect-[4/5] overflow-hidden bg-cream">
                      <img src={it.image} alt="" loading="lazy" className="card-image absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent p-4">
                        <div className="overline text-paper opacity-90">{it.country}</div>
                        <div className="mt-2 font-display text-paper text-lg md:text-xl leading-tight">{it.name}</div>
                        <div className="text-paper/70 text-xs mt-1">{it.position} · {it.company}</div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      );
    },
    editors_picks: () => {
      const meta = sectionMeta("editors_picks");
      if (!meta) return null;
      return (
        <section key="editors_picks" className="py-16 md:py-24 max-w-[1440px] mx-auto px-5 md:px-10" data-testid="editors-picks-section">
          <SectionHeader overline={meta.overline} title={meta.title} lede={meta.lede} />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {articles.slice(2, 4).map((a, i) => (
              <motion.article key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.1 }} className="editorial-card p-6 md:p-8 group" data-testid={`editor-pick-${i}`}>
                <Link to={`/articles/${a.slug}`} className="flex gap-6">
                  <div className="w-24 h-32 md:w-40 md:h-52 flex-shrink-0 relative overflow-hidden bg-paper">
                    <img src={a.image} alt="" loading="lazy" className="card-image absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Quote size={22} strokeWidth={1.2} className="text-ink" />
                    <h3 className="mt-3 font-display text-ink text-xl md:text-2xl leading-tight line-clamp-3 group-hover:text-gold-dark transition-colors">{a.title}</h3>
                    <p className="mt-3 text-ink-soft text-sm leading-relaxed line-clamp-2">{a.excerpt}</p>
                    <div className="mt-4 overline overline-muted">{a.category}</div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </section>
      );
    },
    statistics: () => {
      const meta = sectionMeta("statistics");
      if (!meta) return null;
      const stats = hp.statistics || [];
      return (
        <section key="statistics" className="py-16 md:py-24 bg-cream border-y border-ink-line" data-testid="statistics-section">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-8">
              {stats.map((s, i) => (
                <div key={i} className="border-l border-ink pl-6 md:pl-8" data-testid={`stat-${i}`}>
                  <div className="overline overline-muted mb-3">{`0${i + 1}`}</div>
                  <div className="font-display text-ink text-5xl md:text-6xl lg:text-7xl leading-none tracking-tight">
                    <AnimatedCounter value={s.value} suffix={s.suffix || ""} testId={`stat-value-${i}`} />
                  </div>
                  <div className="mt-4 text-ink-soft text-sm">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    },
    newsletter: () => {
      const meta = sectionMeta("newsletter");
      if (!meta) return null;
      return (
        <section key="newsletter" className="py-16 md:py-24 max-w-[1440px] mx-auto px-5 md:px-10" data-testid="home-newsletter-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="overline mb-6">{meta.overline}</div>
              <h2 className="font-display font-normal text-ink text-4xl md:text-5xl lg:text-[56px] leading-[1.05]">{meta.title}</h2>
              {meta.lede && <p className="mt-5 text-ink-soft text-base md:text-lg max-w-2xl leading-relaxed">{meta.lede}</p>}
            </div>
            <form onSubmit={subscribe} data-testid="home-newsletter-form" className="lg:col-span-5">
              <div className="flex items-center gap-3 border-b border-ink pb-4">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="home-newsletter-input" className="flex-1 bg-transparent outline-none text-ink text-base placeholder:text-ink-faint" />
                <button type="submit" disabled={subscribing} data-testid="home-newsletter-submit" className="text-ink text-sm font-medium tracking-wide hover:text-gold-dark inline-flex items-center gap-2 disabled:opacity-40">
                  Subscribe <ArrowRight size={14} />
                </button>
              </div>
              <p className="mt-3 text-xs text-ink-muted">Unsubscribe at any time. Your email will never be shared.</p>
            </form>
          </div>
        </section>
      );
    },
    partners: () => {
      const meta = sectionMeta("partners");
      if (!meta) return null;
      const partners = hp.partners || [];
      return (
        <section key="partners" className="pb-16 md:pb-24 max-w-[1440px] mx-auto px-5 md:px-10" data-testid="partners-section">
          <div className="overline mb-6">{meta.overline}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-10 items-center">
            {partners.map((p, i) => (
              <motion.div key={p + i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05 }} className="border border-ink-line py-6 px-4 text-center hover:border-ink transition-colors" data-testid={`partner-${i}`}>
                <div className="text-ink-soft hover:text-ink transition-colors text-xs md:text-[13px] font-display italic tracking-wide">{p}</div>
              </motion.div>
            ))}
          </div>
        </section>
      );
    },
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-4 md:pt-10 pb-14 md:pb-20">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className={hero.show_teaser === false ? "lg:col-span-12" : "lg:col-span-8"}>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="font-display font-normal text-ink leading-[0.98] tracking-[-0.02em] text-[52px] sm:text-6xl md:text-7xl lg:text-[104px]"
                dangerouslySetInnerHTML={{ __html: hero.headline_html || "" }}
              />

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="mt-8 max-w-xl text-ink-soft text-base md:text-lg leading-relaxed"
              >
                {hero.subhead}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                {hero.cta_primary && (
                  <Link to={hero.cta_primary_href || "/articles"} data-testid="hero-explore-stories" className="group inline-flex items-center gap-3 px-8 py-4 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors">
                    {hero.cta_primary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
                {hero.cta_secondary && (
                  <Link to={hero.cta_secondary_href || "/get-featured"} data-testid="hero-get-featured" className="group inline-flex items-center gap-3 px-8 py-4 border border-ink text-ink font-medium text-sm tracking-wide rounded-full hover:bg-ink hover:text-paper transition-colors">
                    {hero.cta_secondary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </motion.div>
            </div>

            {hero.show_teaser !== false && featuredMag && (
              <motion.aside initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:col-span-4" data-testid="hero-teaser">
                <div className="relative editorial-card p-5">
                  <div className="aspect-[4/5] relative overflow-hidden mb-5 bg-cream border border-ink-line">
                    <img src={featuredMag.cover_image} alt={`${featuredMag.title} cover`} loading="eager" className="card-image absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="overline overline-muted mb-2">{featuredMag.edition}</div>
                  <div className="font-display text-ink text-xl leading-tight">{featuredMag.caption || featuredMag.title}</div>
                  {featuredMag.cover_subject && <p className="mt-3 text-ink-soft text-sm leading-relaxed">{featuredMag.cover_subject}</p>}
                  <Link to="/magazine" data-testid="hero-cover-story-link" className="mt-5 inline-flex items-center gap-2 text-ink text-sm font-medium hover:text-gold-dark transition-colors">
                    See the issue <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.aside>
            )}
          </div>

          {/* Ticker */}
          {(hero.ticker || []).length > 0 && (
            <div className="mt-14 md:mt-16 overflow-hidden border-y border-ink-line py-4">
              <div className="marquee text-ink-muted text-[11px] font-mono-editorial uppercase tracking-[0.28em]">
                {Array.from({ length: 2 }).flatMap((_, k) => (
                  (hero.ticker || []).map((t, i) => (<span key={`${k}-${i}`} className="whitespace-nowrap">{t}<span className="mx-8 text-ink">◆</span></span>))
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {orderedSections.map((s) => renderers[s.key]?.() || null)}
    </div>
  );
};

export default HomePage;
